import { useQuery } from "@tanstack/react-query";
import ExerciseTagsService from "../../../../../services/ExerciseTagsService";
import { Box, Card, CardActionArea, Typography } from "@mui/material";
import React from "react";
import { TagMenu } from "../../../../../components/exercise-library/tags/ExerciseTagMenu";
import { ExerciseTagDto } from "@growth-machine-llc/stridist-api-client";
import { ConnectionPagination } from "../../../../../components/pagination/ConnectionPagination";
import { NameSortButton } from "../../../../../components/exercise-library/filters/NameSortButton";
import { ManageTagDialog } from "../../../../../components/exercise-library/tags/ManageTagDialog";
import { TagSkeletonCard } from "../../../../../components/card/ExerciseTagSkeletonCard";
import TagChip from "../../../../../components/exercise-library/tags/TagChip";
import { CreateTagAction } from "../../../../../components/exercise-library/buttons/CreateExerciseTagButton";
import { InfoBox } from "../../../../../components/info/InfoBox";
import makeStyles from "@mui/styles/makeStyles";

import { useExerciseListFilterContext } from "../../../../../contexts/ExerciseLibraryFilterContext";
import { TagType } from "../../../../../constants";
import TagCard from "../../../../../components/exercise-library/tags/TagCard";
import OptimisticUpdateContainer from "../../../../../components/loading/OptimisticUpdateContainer";

export const COACH_EXERCISE_TAGS_LIST_QUERY_KEY = "coach-exercise-tags-list";

const MAX_WIDTH = 400;

const useStyles = makeStyles(() => ({
  createTagButton: {
    width: "100%",
    maxWidth: MAX_WIDTH,
  },
}));

const ITEMS_PER_PAGE = 5;

export const CoachExerciseTagsListRoute = () => {
  const s = useStyles();

  const [page, setPage] = React.useState(1);
  const perPage = ITEMS_PER_PAGE;

  const handlePageChange = React.useCallback((cursor: number) => {
    setPage(cursor);
  }, []);

  const { query, sortKey, handleSortKeyChange } =
    useExerciseListFilterContext();

  const { data: coachTags, isLoading } = useQuery({
    queryKey: [
      COACH_EXERCISE_TAGS_LIST_QUERY_KEY,
      { page, perPage, query, sortKey },
    ],
    queryFn: () =>
      ExerciseTagsService.getExerciseTags(page, perPage, query, sortKey),
  });

  const [manageTagDialogOpen, setManageTagDialogOpen] = React.useState(false);
  const [selectedExerciseTag, setSelectedExerciseTag] =
    React.useState<ExerciseTagDto | null>();

  const handleCloseExerciseTagDialog = React.useCallback(() => {
    setManageTagDialogOpen(false);
    setSelectedExerciseTag(null);
  }, []);

  return (
    <Box display={"flex"} flexDirection="column" gap={2}>
      <Box>
        <NameSortButton
          sortKey={sortKey}
          onChangeSortKey={handleSortKeyChange}
        />
      </Box>
      {isLoading || !coachTags ? (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <TagSkeletonCard key={index} />
          ))}
        </>
      ) : coachTags.items.length > 0 ? (
        coachTags.items.map((tag) => (
          <OptimisticUpdateContainer key={tag.id} id={tag.id}>
            <TagCard
              tag={tag}
              onEdit={() => {
                setSelectedExerciseTag(tag);
                setManageTagDialogOpen(true);
              }}
              onClick={() => {
                setSelectedExerciseTag(tag);
                setManageTagDialogOpen(true);
              }}
              tagType={TagType.EXERCISE}
            />
          </OptimisticUpdateContainer>
        ))
      ) : coachTags.totalCount > 0 ? (
        <Box display={"flex"} justifyContent={"center"} padding={2}>
          <Typography> No tags found for current filters.</Typography>
        </Box>
      ) : (
        <>
          <InfoBox
            title={"No exercise tags found"}
            subtitle={"Create a tag to classify you exercises."}
          >
            <CreateTagAction
              useThemePalette={false}
              className={s.createTagButton}
              tagType={TagType.EXERCISE}
            />
          </InfoBox>
        </>
      )}
      {manageTagDialogOpen && (
        <ManageTagDialog
          open={manageTagDialogOpen}
          tag={selectedExerciseTag}
          onClose={handleCloseExerciseTagDialog}
          tagType={TagType.EXERCISE}
        />
      )}

      <ConnectionPagination
        currentPage={page || 1}
        pages={coachTags?.totalPages || 1}
        gotoPage={handlePageChange}
        autoHide
      />
    </Box>
  );
};
